import {
  React,
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  BarController,
  LineElement,
  Title,
  Tooltip,
  Legend
);


import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  useTheme,
  FormControlLabel,
  FormControl,
  colors
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import LoadingOverlay from 'react-loading-overlay-nextgen';
import { transpose, getMonths } from 'src/utils/miscTools';
import { getWattsAsKilowattsPer15Seconds } from 'src/utils/calculations';
import {months_aggregate_url} from 'src/utils/constants';

function getData(token, deviceId, dateOrdinal, year,trackingOnly, range, setDaysincident, setDayscollected, setDaysreadingtimes, setEfficiencies, setIsLoading) {
  setIsLoading(true);
  const postBody = JSON.stringify({
      AGGREGATE_PERIOD: 'DAY',
      DATE_RANGE: 'MONTH',
      DATE_ORDINAL: dateOrdinal,
      YEAR: year,
      DISH_IDS: deviceId,
      RANGE: range,
      TRACKING_ONLY: trackingOnly
    })
  fetch(months_aggregate_url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Token ' + token
    },
    body: postBody
  }).then((response) => {
    if (response.ok) return response.json();
    throw response;
  }).then((json) => {
    // console.log(json);
    if (json.length > 0) {
      const transposed = transpose(json);
      setDaysreadingtimes(transposed[0]);
      setDaysincident(transposed[1]);
      setDayscollected(transposed[2]);
      setEfficiencies(transposed[3]);
      setIsLoading(false);
    } else {
       setDaysreadingtimes([]);
      setDaysincident([]);
      setDayscollected([]);
      setEfficiencies([]);
      setIsLoading(false);
      // console.error(json);
    }
  }).catch((err) => {
    console.error(err);
    // setError(err);
  });
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
  },
}));

const AggregatePerformance = (props) => {
  const { deviceId, token } = props;

  const [dateOrdinal, setDateOrdinal] = useState('lastThirty');
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [range, setRange] = useState('Peak Hours');
  const [trackingDataOnly, setTrackingDataOnly] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [daysincident, setDaysincident] = useState([]);
  const [dayscollected, setDayscollected] = useState([]);
  const [efficiencies, setEfficiencies] = useState([]);
  const [daysreadingtimes, setDaysreadingtimes] = useState([]);

  function onRangeChange(selectedRange) {
    setRange(selectedRange);
    getData(token, deviceId, dateOrdinal, selectedYear,trackingDataOnly, selectedRange, setDaysincident, setDayscollected, setDaysreadingtimes, setEfficiencies, setIsLoading);
  }

  function onTrackingOnlyChange() {
    const changedValue = !trackingDataOnly;
    setTrackingDataOnly(changedValue);
    getData(token, deviceId, dateOrdinal, selectedYear,changedValue, range, setDaysincident, setDayscollected, setDaysreadingtimes, setEfficiencies, setIsLoading);
  }

  function onDateChange(dateValue) {
    setDateOrdinal(dateValue.split('-')[0]);
    if (dateValue ==='lastThirty'){
      setSelectedYear(currentYear);
      getData(token, deviceId, 'lastThirty', currentYear, range, setDaysincident, setDayscollected, setDaysreadingtimes, setEfficiencies, setIsLoading);
    }else{
      setSelectedYear(dateValue.split('-')[1]);
      getData(token, deviceId, dateValue.split('-')[0], dateValue.split('-')[1], range, setDaysincident, setDayscollected, setDaysreadingtimes, setEfficiencies, setIsLoading);

    }
    
    getData(token, deviceId, dateValue.split('-')[0], dateValue.split('-')[1], range, setDaysincident, setDayscollected, setDaysreadingtimes, setEfficiencies, setIsLoading);
  }

  useEffect(() => {
    if (deviceId.length > 0 && token) {
      getData(token, deviceId, dateOrdinal, currentYear,trackingDataOnly, range, setDaysincident, setDayscollected, setDaysreadingtimes, setEfficiencies, setIsLoading);
    }
  }, [deviceId]);

  const theme = useTheme();


  const incidentKW = daysincident.map((e) => getWattsAsKilowattsPer15Seconds(e));
  const incidentEnergyDataset = {
    labels: daysreadingtimes,
    label: 'Incident Energy',
    borderColor: colors.blue[500],
    borderWidth: 2,
    data: incidentKW,
    type: 'bar'
  };

  const collectedKW = dayscollected.map((e) => getWattsAsKilowattsPer15Seconds(e));
  const enthalpyChangeDataset = {
    labels: daysreadingtimes,
    label: 'Energy Collected',
    borderColor: colors.red[500],
    borderWidth: 2,
    data: collectedKW,
    type: 'bar'
  };

  let i;
  for (i = 0; i < efficiencies.length; i++) {
    const incident_energy = incidentKW[i];
    const fluid_enthalpy = collectedKW[i];
    let efficiencyPercentage = fluid_enthalpy / incident_energy * 100
    efficiencies[i] = efficiencyPercentage
    if (efficiencies[i] <=  100 && efficiencies[i] >0) {
      efficiencies[i] = efficiencyPercentage;
    }else{
      efficiencies[i] = null;
    }
  }

  const efficiencyDataset = {
    labels: daysreadingtimes,
    label: 'Efficiency',
    pointRadius: 3,
    borderColor: colors.green[500],
    borderWidth: 2,
    data: efficiencies,
    type: 'line'
  };

  const data = {
    datasets: [
      incidentEnergyDataset,
      enthalpyChangeDataset,
      efficiencyDataset
    ],
    labels: daysreadingtimes
  };

  const options = {
    animation: false,
    cornerRadius: 20,
    layout: { padding: 0 },
    legend: { display: true },
    maintainAspectRatio: false,
    responsive: true,
    /*scales: {
    xAxes: [{
      id: 'x',
      type: 'time',
      display: true,
      title: {
        display: true,
        text: 'Date'
      },
      ticks: {
        major: {
          enabled: true
        },
        font: function(context) {
          if (context.tick && context.tick.major) {
            return {
              weight: 'bold',
              color: '#FF0000'
            };
          }
        }
      }
    }],
    yAxes: [{
      id: 'y',
      display: true,
      title: {
        display: true,
        text: 'value'
      }
    }]
  },*/
    tooltips: {
      backgroundColor: theme.palette.background.paper,
      bodyFontColor: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
      borderWidth: 1,
      enabled: true,
      footerFontColor: theme.palette.text.secondary,
      intersect: true,
      mode: 'index',
      titleFontColor: theme.palette.text.primary
    }
  };

  const classes = useStyles();
  const months = getMonths();

  return (
    <Card>
      <CardHeader
        action={(
          <div>
            <FormControl component="fieldset">
            <FormControlLabel 
              control={
                <Checkbox
                  defaultChecked 
                  onChange={
                    (e) => onTrackingOnlyChange(e.target.value)}/>
                  } 
                  label="Tracking Data Only" 
                />
            </FormControl>
            <FormControl shrink="true" variant="outlined" className={classes.formControl}>
              <FormLabel component="legend">Select Period</FormLabel>
              <Select
                autoWidth
                onChange={(e) => onDateChange(e.target.value)}
                defaultValue="lastThirty"
              >
                <MenuItem key="lastThirty" value="lastThirty">Last 30 Days</MenuItem>
                {
                months.map(({ month, year, text }) => (
                  <MenuItem key={month} value={`${month}-${year}`}>{text}</MenuItem>
                ))
              }
              </Select>
            </FormControl>
            &nbsp;&nbsp;
            <FormControl component="fieldset">
              <FormLabel component="legend">Range</FormLabel>
              <RadioGroup
                row
                aria-label="range"
                name="row-radio-buttons-group"
                onChange={(e) => onRangeChange(e.target.value)}
              >
                <FormControlLabel value="Morn-Eve" control={<Radio checked={range === 'Morn-Eve'} />} label="Morn-Eve" />
                <FormControlLabel value="Peak Hours" control={<Radio checked={range === 'Peak Hours'} />} label="Peak Hours" />
                <FormControlLabel value="24 Hours" control={<Radio checked={range === '24 Hours'} />} label="24 Hours" />
              </RadioGroup>
            </FormControl>
          </div>
        )}
        title="Aggregate Performance (KiloWatt-Hours)"
      />
      <Divider />
      <CardContent>
        <Box
          sx={{
            height: 500,
            position: 'relative'
          }}
        >
          <LoadingOverlay
            active={isLoading}
            spinner
            text="Loading Data..."
            styles={{
              wrapper: (base) => ({
                ...base,
                height: 500,
              })
            }}
          >
            <Line
              data={data}
              options={options}
            />
          </LoadingOverlay>
        </Box>
      </CardContent>
      <Divider />
    </Card>
  );
};

AggregatePerformance.propTypes = {
  deviceId: PropTypes.string,
  token: PropTypes.string,
  deviceId: PropTypes.array
};

export default AggregatePerformance;
